import React, { useEffect, useState } from "react";
import GlobalBanner from "./GlobalBanner";
import { FaRegStar } from "react-icons/fa";
import { MdArrowBackIos, MdArrowForwardIos } from "react-icons/md";
import { useMediaQuery } from "react-responsive";
import { useSelectedLocation } from "../context/LocationContext";
import { NEEMUCH_GALLERY } from "../data/neemuchGallery";

function Gallery() {
  const { locationId } = useSelectedLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  useEffect(() => {
    if (locationId === "neemuch") {
      setGallery(NEEMUCH_GALLERY);
      setError(null);
      setLoading(false);
      return;
    }

    const fetchGallery = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          "https://secondhomemds.com/adminpanel/Welcome/getreq/gallery"
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log(data);

        // Access the all_data property which contains the array
        if (!Array.isArray(data.all_data)) {
          throw new Error("API response does not contain an array in all_data");
        }

        setGallery(data.all_data); // Use data.all_data instead of data
      } catch (err) {
        console.error("Error fetching gallery:", err);
        setError(err.message);
        setGallery([]);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [locationId]);

  // Calculate featured indexes for the mobile grid layout (1 big, then 4 small)
  const getMobileFeaturedIndexes = () => {
    let featuredIndexes = [];
    for (let i = 0; i < gallery.length; i += 5) {
      featuredIndexes.push(i);
    }
    return featuredIndexes;
  };

  const mobileFeaturedIndexes =
    gallery.length > 0 ? getMobileFeaturedIndexes() : [];

  // Original featured indexes calculation for desktop (unchanged)
  const getFeaturedIndexes = () => {
    let featuredIndexes = [];
    let i = 0;
    let step = 5;

    while (i < gallery.length) {
      featuredIndexes.push(i);
      i += step;
      step = step === 5 ? 4 : 5;
    }

    return featuredIndexes;
  };

  const featuredIndexes = gallery.length > 0 ? getFeaturedIndexes() : [];

  useEffect(() => {
    const handleKey = (e) => {
      if (!isOpen || gallery.length === 0) return;
      if (e.key === "ArrowRight") {
        setPhotoIndex((prev) => (prev + 1) % gallery.length);
      } else if (e.key === "ArrowLeft") {
        setPhotoIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
      } else if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, gallery]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl">Loading gallery...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-xl text-red-500">Error: {error}</div>
      </div>
    );
  }

  return (
    <div>
      <GlobalBanner
        title="Gallery"
        subtitle="Gallery"
        bgImage="/images/secondhome_gallery.jpeg"
      />
      <div className="py-10 px-4 xl:p-10">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Gallery</h1>
        </div>

        {gallery.length === 0 && !loading ? (
          <div className="text-center text-xl">No gallery available</div>
        ) : (
          <>
            {isMobile ? (
              // Mobile layout: 1 big image followed by 4 small images
              <div className="grid grid-cols-2 gap-1 w-full auto-rows-[150px]">
                {gallery.map((item, index) => {
                  const isFeatured = mobileFeaturedIndexes.includes(index);
                  const gridSpan = isFeatured ? "col-span-2 row-span-2" : "";
                  return (
                    <div
                      key={item.id || index}
                      className={`relative overflow-hidden ${gridSpan}`}
                    >
                      <img
                        // src={item.imgurl}
                        src={item.imgurl.replace(
  "https://adminpanel.secondhomemds.com",
  "https://secondhomemds.com/adminpanel"
)}
                        alt={item.img_title}
                        className="w-full h-full object-cover cursor-pointer"
                        loading="lazy"
                        onClick={() => {
                          setIsOpen(true);
                          setPhotoIndex(index);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              // Desktop layout: EXACTLY AS IT WAS BEFORE
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-1 w-full auto-rows-[200px] sm:auto-rows-[250px]">
                {gallery.map((item, index) => {
                  const isFeatured = featuredIndexes.includes(index);
                  const gridSpan = isFeatured ? "col-span-2 row-span-2" : "";
                  return (
                    <div
                      key={item.id || index}
                      className={`relative overflow-hidden group ${gridSpan}`}
                    >
                      <img
                        // src={item.imgurl}
                        src={item.imgurl.replace(
  "https://adminpanel.secondhomemds.com",
  "https://secondhomemds.com/adminpanel"
)}
                        alt={item.img_title}
                        className="w-full h-full transition-transform duration-500 group-hover:scale-105 object-cover cursor-pointer"
                        loading="lazy"
                        onClick={() => {
                          setIsOpen(true);
                          setPhotoIndex(index);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {isOpen && gallery.length > 0 && (
              <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center p-4">
                <button
                  className="absolute top-4 right-4 text-white text-2xl"
                  onClick={() => setIsOpen(false)}
                >
                  ✕
                </button>

                <button
                  onClick={() =>
                    setPhotoIndex(
                      (photoIndex - 1 + gallery.length) % gallery.length
                    )
                  }
                  className="absolute left-4 text-white text-4xl"
                >
                  <MdArrowBackIos />
                </button>
                <button
                  onClick={() =>
                    setPhotoIndex((photoIndex + 1) % gallery.length)
                  }
                  className="absolute right-4 text-white text-4xl"
                >
                  <MdArrowForwardIos />
                </button>

                <div className="max-w-3xl w-full flex justify-center mb-4">
                  <img
                    src={gallery[photoIndex]?.imgurl.replace(
  "https://adminpanel.secondhomemds.com",
  "https://secondhomemds.com/adminpanel"
)}
                    alt={gallery[photoIndex]?.img_title}
                    className="max-h-[70vh] object-contain"
                  />
                </div>

                <div className="text-white text-center mb-4">
                  <h3 className="text-xl font-bold">
                    {gallery[photoIndex]?.img_title}
                  </h3>
                  {gallery[photoIndex]?.price && (
                    <p className="text-lg">
                      Price: ₹{gallery[photoIndex]?.price}
                    </p>
                  )}
                </div>

                <div className="flex gap-2 overflow-x-auto w-full max-w-4xl py-2 px-4 min-h-fit bg-black/60 rounded">
                  {gallery.map((item, idx) => (
                    <img
                      key={idx}
                      src={item.imgurl.replace(
  "https://adminpanel.secondhomemds.com",
  "https://secondhomemds.com/adminpanel"
)}  
                      alt={item.img_title}
                      onClick={() => setPhotoIndex(idx)}
                      className={`w-20 h-16 object-cover cursor-pointer border-2 ${
                        idx === photoIndex
                          ? "border-white"
                          : "border-transparent"
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Gallery;
